import { ClientSteps, Statistic, ProgressEnum } from "../../Constants";

/**
 * Service for handling typography for statistics component.
 */
export const StatisticLabels = {
    resolveNotStartedLabelForLegend,
    resolveInProgressLabelForLegend,
    resolveCompletedLabelForLegend,
    isActiveStateForUserStep,
    resolveHeadingLabel,
}

/**
 * Resolves the label for the "not started" state in the legend,
 * based on the type of statistic.
 *
 * @param {string} state - The statistics type identifier.
 *
 * @returns {string} The localized label for the "not started" legend.
 */
function resolveNotStartedLabelForLegend(state) {
    if ([
        Statistic.ClientAgreementStatistics,
        Statistic.ClientSecondAgreementStatistics
    ].includes(state)) {
        return "Jeszcze nie podpisało umowy";
    }

    if ([
        Statistic.YourDevelopmentGoalStatistics,
        Statistic.YourSecondDevelopmentGoalStatistics,
    ].includes(state)) {
        return "Jeszcze nie zamówiło bonów";
    }

    return "Nie zaczęło";
}

/**
 * Resolves the label for the "in progress" state in the legend,
 * based on the type of statistic.
 *
 * @param {string} state - The statistics type identifier.
 *
 * @returns {string} The localized label for the "in progress" legend.
 */
function resolveInProgressLabelForLegend() {

    return "W trakcie";
}

/**
 * Resolves the label for the "completed" state in the legend,
 * based on the type of statistic.
 *
 * @param {string} state - The statistics type identifier.
 *
 * @returns {string} The localized label for the "completed" legend.
 */
function resolveCompletedLabelForLegend(state) {
    if ([
        Statistic.ClientAgreementStatistics,
        Statistic.ClientSecondAgreementStatistics].includes(state)
    ) {
        return "Podpisało umowę";
    }

    if ([
        Statistic.YourDevelopmentGoalStatistics,
        Statistic.YourSecondDevelopmentGoalStatistics,
    ].includes(state)) {
        return "Dokonało zamówienia";
    }

    return "Ukończyło";
}

/**
 * Determines whether a given userStep is active for a specific statistic state and step.
 *
 * @param {Statistic} state - The current statistic state to evaluate.
 * @param {string} clientStep - The current client step of the user.
 * @param {"completed" | "in-progress" | "not-started"} step - The current progress step.
 * @returns {boolean} - Returns true if the userStep is considered active for the given state and step.
 */
function isActiveStateForUserStep(state, clientStep, step) {
    const stateStepMap = {
        [Statistic.AutoAnalysisStatistics]: {
            [ProgressEnum.Completed.value]: [ClientSteps.AutoAnalysisCompleted.value],
            [ProgressEnum.InProgress.value]: [ClientSteps.AutoAnalysisInProgress.value],
            [ProgressEnum.NotStarted.value]: [ClientSteps.AutoAnalysisNotStarted.value],
        },
        [Statistic.SelfEsteemStatistics]: {
            [ProgressEnum.Completed.value]: [ClientSteps.SelfEsteemCompleted.value],
            [ProgressEnum.InProgress.value]: [ClientSteps.SelfEsteemInProgress.value],
            [ProgressEnum.NotStarted.value]: [ClientSteps.SelfEsteemNotStarted.value],
        },
        [Statistic.YourDevelopmentGoalStatistics]: {
            [ProgressEnum.Completed.value]: [ClientSteps.OrderCompleted.value],
            [ProgressEnum.NotStarted.value]: [ClientSteps.OrderNotStarted.value],
        },
        [Statistic.ClientAgreementStatistics]: {
            [ProgressEnum.Completed.value]: [ClientSteps.ContractSigned.value],
            [ProgressEnum.NotStarted.value]: [ClientSteps.ContractNotSigned.value],
        },
        [Statistic.YourSecondDevelopmentGoalStatistics]: {
            [ProgressEnum.Completed.value]: [ClientSteps.OrderSecondCompleted.value],
            [ProgressEnum.NotStarted.value]: [ClientSteps.OrderSecondNotStarted.value],
        },
        [Statistic.ClientSecondAgreementStatistics]: {
            [ProgressEnum.Completed.value]: [ClientSteps.ContractSecondSigned.value],
            [ProgressEnum.NotStarted.value]: [ClientSteps.ContractSecondNotSigned.value],
        },
    };

    const steps = stateStepMap[state]?.[step];
    return steps?.includes(clientStep) ?? false;
}

/**
 * Resolves the heading label component for a given statistics state.
 *
 * @param {string} state - The statistics type identifier (e.g., Statistic.ClientAgreementStatistics).
 *
 * @returns {JSX.Element} A JSX element to be rendered as the heading.
 */
function resolveHeadingLabel(state, data) {
    const noTimeLeft = data.daysLeft == '';
    const showTime = data.daysLeft != null && data.daysLeft != 'null' && !data.isCompleted;

    switch (state) {
        case Statistic.AutoAnalysisStatistics:
            return (
                <p>
                    <span className="bold">{data.percent}% osób</span> w projekcie <span className="bold">już ukończyło</span> autoanalizę.
                    {showTime && noTimeLeft && <span> Twój czas na dokończenie autoanalizy minął.</span>}
                    {showTime && !noTimeLeft && data.isActive && <span> Masz jeszcze <span className="bold"> {data.daysLeft} </span> na dokończenie autoanalizy.</span>}
                </p>
            );

        case Statistic.SelfEsteemStatistics:
            return (
                <p>
                    <span className="bold">{data.percent}% osób</span> w projekcie <span className="bold">już ukończyło</span> samoocenę.

                    {showTime && noTimeLeft && <span> Twój czas na dokończenie samooceny minął.</span>}
                    {showTime && !noTimeLeft && data.isActive && <span> Masz jeszcze <span className="bold">{data.daysLeft}</span> na dokończenie samooceny.</span>}
                </p>
            );

        case Statistic.YourDevelopmentGoalStatistics:
        case Statistic.YourSecondDevelopmentGoalStatistics:
            return (
                <p>
                    <span className="bold">{data.percent}% osób</span> w projekcie <span className="bold">już dokonało</span> zamówienia bonów.
                </p>
            );

        case Statistic.ClientAgreementStatistics:
        case Statistic.ClientSecondAgreementStatistics:
            return (
                <p>
                    <span className="bold">{data.percent}% osób</span> w projekcie <span className="bold">już podpisało</span> umowę o dofinansowanie.
                </p>
            );

        default:
            return (
                <p></p>
            );
    }
}